import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectShell from "@/pages/ProjectShell";
import ProjectExpensesPage from "@/pages/ProjectExpensesPage";
import ProjectShotsPage from "@/pages/ProjectShotsPage";
import ProjectFilesPage from "@/pages/ProjectFilesPage";
import ProjectMembersPage from "@/pages/ProjectMembersPage";
import ProjectPlanPage from "@/pages/ProjectPlanPage";
import ProjectDiscussionPage from "@/pages/ProjectDiscussionPage";
import ProjectUpdatesPage from "@/pages/ProjectUpdatesPage";
import CalendarPage from "@/pages/CalendarPage";
import LocationsPage from "@/pages/LocationsPage";
import TeamPage from "@/pages/TeamPage";
import CallSheetPage from "@/pages/CallSheetPage";
import CallSheetsListPage from "@/pages/CallSheetsListPage";
import ConfirmPage from "@/pages/ConfirmPage";
import PublicCallSheetPage from "@/pages/PublicCallSheetPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminWebhooksPage from "@/pages/AdminWebhooksPage";
import AdminWeeklyUpdatesPage from "@/pages/AdminWeeklyUpdatesPage";
import StudioResourcesPage from "@/pages/StudioResourcesPage";
import StudioPublicSharePage from "@/pages/StudioPublicSharePage";
import LaunchpadPage from "@/pages/LaunchpadPage";
import MyTasksPage from "@/pages/MyTasksPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandProvider } from "@/lib/brand";

function GateGuard({ children }) {
  const { user, status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }
  if (status !== "authed") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Client users have a small allow-list of paths so they can peek at
  // Launchpad / Projects / My Tasks (scoped server-side to just the
  // projects they're members of), plus their own client home. Any other
  // path bounces to /clients/{their_client_id}.
  if (user?.client_id) {
    const home = `/clients/${user.client_id}`;
    const p = location.pathname;
    const allowed =
      p === "/" ||
      p === "/launchpad" ||
      p === "/my-tasks" ||
      p.startsWith("/launchpad/") ||
      p.startsWith("/my-tasks/") ||
      p.startsWith("/projects/") ||
      p.startsWith(home);
    if (!allowed) {
      return <Navigate to={home} replace />;
    }
  }
  return children;
}

// State-preserving redirect. `<Navigate>` alone drops `location.state`
// on redirect, which used to break the "Back to Client" breadcrumb when
// a project card at `/clients/:id` navigated to `/projects/:id` (the
// index route immediately re-navigated to `/plan` and discarded the
// `from` state carried by the click).
function IndexRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={to} replace state={location.state} />;
}

function App() {
  return (
    <div className="App grain min-h-screen bg-zinc-950 text-zinc-100">
      <BrowserRouter>
        <AuthProvider>
          <BrandProvider>
          <Routes>
            {/* Public — no gate */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/signup/:token" element={<AcceptInvitePage />} />
            <Route path="/confirm/:csId/:token" element={<ConfirmPage />} />
            <Route path="/share/call-sheet/:csId" element={<PublicCallSheetPage />} />
            <Route path="/share/studio/:token" element={<StudioPublicSharePage />} />
            {/* Gated app */}
            <Route
              element={
                <GateGuard>
                  <Layout />
                </GateGuard>
              }
            >
              <Route path="/" element={<ProjectsPage />} />
              <Route path="/launchpad" element={<LaunchpadPage />} />
              <Route path="/my-tasks" element={<MyTasksPage />} />
              <Route path="/projects/:id" element={<ProjectShell />}>
                <Route index element={<IndexRedirect to="plan" />} />
                <Route path="plan" element={<ProjectPlanPage />} />
                <Route path="shots" element={<ProjectShotsPage />} />
                <Route path="files" element={<ProjectFilesPage />} />
                <Route path="members" element={<ProjectMembersPage />} />
                <Route path="call-sheet" element={<CallSheetPage />} />
                <Route path="expenses" element={<ProjectExpensesPage />} />
                <Route path="discussion" element={<ProjectDiscussionPage />} />
                <Route path="updates" element={<ProjectUpdatesPage />} />
              </Route>
              <Route path="/call-sheets" element={<CallSheetsListPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/locations" element={<LocationsPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/studio" element={<StudioResourcesPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              {/* Legacy entry — top-level /planning-calendar now bounces
                  staff to the client index (feature lives on client pages).
                  Client users are already redirected by GateGuard before
                  reaching this route, so this only ever runs for staff. */}
              <Route path="/planning-calendar" element={<Navigate to="/clients" replace />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/settings" element={<AdminSettingsPage />} />
              <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />
              <Route path="/admin/weekly-updates" element={<AdminWeeklyUpdatesPage />} />
              {/* Catch-all inside the gated tree — if a client user
                  navigates to an undeclared path, GateGuard on the
                  parent won't fire (no route match). Bouncing to "/"
                  routes through GateGuard which then redirects clients
                  to their /clients/{id} home. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </BrandProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#18181b",
            border: "1px solid #27272a",
            color: "#f4f4f5",
          },
        }}
      />
    </div>
  );
}

export default App;
